import {FormEvent, useRef, useState} from 'react';
import {EnvelopeIcon, UserPlusIcon} from "@heroicons/react/24/outline";

import {User} from "../../../../../api/accessControl/User.ts";
import {Input} from "../../../../../components/general/Input.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {useTeammates} from "./useTeammates.ts";

const ROLE_LABELS: Record<string, string> = {
    entrepreneur: 'Ondernemer',
    partner: 'Boekhouder',
    admin: 'Beheerder',
};

function formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('nl-NL', {day: 'numeric', month: 'short', year: 'numeric'});
}

interface TeammatesTabProps {
    currentUser: User;
}

export function TeammatesTab({currentUser}: TeammatesTabProps) {
    const {
        isLoading,
        hasLoadError,
        members,
        pendingInvitations,
        reload,
        inviteTeammate,
        resendInvitation,
    } = useTeammates(currentUser);

    const formRef = useRef<HTMLFormElement>(null);
    const [email, setEmail] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [resendingEmail, setResendingEmail] = useState<string | null>(null);

    async function handleInvite(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        const success = await inviteTeammate(email);
        if (success) setEmail('');
        setIsSubmitting(false);
    }

    async function handleResend(invitationEmail: string) {
        if (resendingEmail) return;
        setResendingEmail(invitationEmail);
        await resendInvitation(invitationEmail);
        setResendingEmail(null);
    }

    return (
        <div className="space-y-8">
            <div className="rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900">Teamlid uitnodigen</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Nodig een collega uit om samen te werken in het partnerportaal.
                </p>

                <form ref={formRef} onSubmit={handleInvite} className="mt-4 flex items-end gap-2">
                    <div className="flex-1">
                        <Input
                            type="email"
                            required
                            placeholder="email@adres.nl"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <PrimaryButton
                        title="Uitnodigen"
                        icon={UserPlusIcon}
                        onClick={() => formRef.current?.requestSubmit()}
                        disabled={isSubmitting}
                        className="h-[38px]"
                    />
                </form>
            </div>

            <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">Teamleden</h3>
                {isLoading ? (
                    <p className="text-sm text-gray-500">Laden...</p>
                ) : hasLoadError ? (
                    <div>
                        <p className="text-sm text-gray-500">De teamleden konden niet worden geladen.</p>
                        <SecondaryButton className="mt-3" title="Opnieuw proberen" onClick={() => reload()}/>
                    </div>
                ) : members.length === 0 ? (
                    <p className="text-sm text-gray-500">Nog geen teamleden.</p>
                ) : (
                    <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                        {members.map((member) => (
                            <li key={member.id} className="flex items-center gap-3 p-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-superliteprimary text-xs font-semibold text-primary">
                                    {member.first_name.charAt(0).toUpperCase()}{member.last_name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900">
                                        {member.first_name} {member.last_name}
                                        <span className="ml-2 text-xs font-normal text-gray-500">
                                            {ROLE_LABELS[member.role] ?? member.role}
                                        </span>
                                    </p>
                                    <p className="text-sm text-gray-500">{member.email}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">Openstaande uitnodigingen</h3>
                {isLoading ? (
                    <p className="text-sm text-gray-500">Laden...</p>
                ) : hasLoadError ? (
                    <p className="text-sm text-gray-500">De uitnodigingen konden niet worden geladen.</p>
                ) : pendingInvitations.length === 0 ? (
                    <p className="text-sm text-gray-500">Geen openstaande uitnodigingen.</p>
                ) : (
                    <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                        {pendingInvitations.map((invitation) => (
                            <li key={`${invitation.email}-${invitation.created_at}`} className="flex items-center justify-between gap-3 p-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0"/>
                                    <div className="min-w-0">
                                        <p className="text-sm text-gray-900">{invitation.email}</p>
                                        <p className="text-xs text-gray-400">Verstuurd {formatDate(invitation.created_at)}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleResend(invitation.email)}
                                    disabled={resendingEmail !== null}
                                    className="text-sm font-medium text-primary hover:underline flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                                >
                                    {resendingEmail === invitation.email ? 'Versturen...' : 'Opnieuw versturen'}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
